import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle, ArrowRight } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { DueReminderModal } from './DueReminderModal';

/**
 * One still line naming how many invoices (and, for whoever sees loans, loan
 * EMIs) are overdue or due soon, shown only
 * while there are some, on an amber background that sweeps left to right. It polls for the counts alone; the popup it
 * opens loads the invoices themselves.
 */
export function DueReminderBanner() {
  const { user, companyId, can } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ['due-reminders', companyId],
    queryFn: () => api.invoices.dueReminders({ companyId }),
    enabled: !!user && can('invoice:read'),
    refetchInterval: 60_000,
  });
  const { data: loans } = useQuery({
    queryKey: ['due-reminders', companyId, 'loans'],
    queryFn: () => api.loans.dueReminders({ companyId }),
    enabled: !!user && can('loan:read'),
    refetchInterval: 60_000,
  });

  const overdue = (data?.counts.overdue ?? 0) + (loans?.counts.overdue ?? 0);
  const dueSoon = (data?.counts.dueSoon ?? 0) + (loans?.counts.dueSoon ?? 0);
  const reminderDays = Math.max(data?.reminderDays ?? 0, loans?.reminderDays ?? 0);

  // Hide the banner when nothing is overdue or due soon, or when reminders are off.
  if (reminderDays <= 0 || overdue + dueSoon === 0) {
    return null;
  }

  const parts: string[] = [];
  if (overdue > 0) parts.push(`${overdue} overdue`);
  if (dueSoon > 0) parts.push(`${dueSoon} due soon`);
  const summaryText = parts.join(', ');
  const what = loans?.counts.total
    ? data?.counts.total
      ? 'invoices and loan EMIs'
      : 'loan EMIs'
    : 'invoices';
  const bannerText = `${summaryText} — click to view ${what}`;

  return (
    <>
      <aside role="region" aria-label="Due date reminders" className="relative z-30 shrink-0">
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="banner-sweep flex h-8 w-full items-center gap-3 px-3 text-xs text-white shadow-sm transition-opacity hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/70 sm:px-6"
          aria-label={`Due date reminder: ${summaryText}. Click to review.`}
        >
          <span className="flex shrink-0 items-center gap-1 font-bold">
            {overdue > 0 ? (
              <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            ) : (
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            )}
            <span>Reminder:</span>
          </span>

          {/* Said once and held still; the background's sweep is what catches the eye. */}
          <span className="min-w-0 flex-1 truncate text-left font-semibold">{bannerText}</span>

          <span className="hidden shrink-0 items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-white/90 sm:inline-flex">
            View
            <ArrowRight className="h-3 w-3" aria-hidden="true" />
          </span>
        </button>
      </aside>

      {modalOpen ? <DueReminderModal onClose={() => setModalOpen(false)} /> : null}
    </>
  );
}
